import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { of } from 'rxjs';
import { SepaAPI } from '../api/SepaAPI.js';
import { SepaMandat } from '../entities/SepaMandat.js';
import { getMandateLifecycle, type MandateSection } from './mandateLifecycle.js';
import { SepaCardSkeleton, SepaMandateCard } from './SepaMandateCard.js';

/**
 * `repeat` drops entries that share a key, so a booking carrying two mandates for the same billing
 * position used to render as a single card - exactly the duplicate that has to be visible here. Only
 * templates have no `_id` yet, and those are unique per booking and billing position by construction.
 */
export const uniqueID = (mandat: Pick<SepaMandat, '_id' | 'bookingID' | 'name'>) =>
  mandat._id ?? `${mandat.bookingID}-${mandat.name}`;

/**
 * A person's stored mandates and templates, each once. A template that takes a stored row over
 * carries the row's Mandatsreferenz (`generateSepaMandatTemplates`). An ended row stays in the
 * archive - the template's hint names it, so it has to be there to find. A stored draft is replaced
 * by its template instead: both would wait for consent.
 */
export const withTemplates = <T extends Pick<SepaMandat, 'accepted' | 'mandateReference'>>(
  mandates: T[],
  templates: T[],
): T[] => {
  const references = new Set(templates.map(template => template.mandateReference).filter(isNotNullish));
  return [
    ...templates,
    ...mandates.filter(
      mandat => mandat.accepted || !mandat.mandateReference || !references.has(mandat.mandateReference),
    ),
  ];
};

export const UserSepaMandateList = stateful((props: { userID: Maybe<string> }) => {
  const { value: sepaMandats, loading: sepaMandatsLoading } = useQuery(
    () => (props.userID ? SepaAPI.subscribeMandats(props.userID)(selectionSet(() => SepaMandat, 1)) : of([])),
    [props.userID],
  );

  const { value: sepaMandatTemplates, loading: sepaMandatTemplatesLoading } = useQuery(
    () => (props.userID ? SepaAPI.getTemplates(props.userID)(selectionSet(() => SepaMandat, 1)) : of([])),
    [props.userID, sepaMandats],
  );

  const { value: faultyMandats, loading: faultyMandatsLoading } = useQuery(
    () => (props.userID ? SepaAPI.subscribeFaultyMandats(props.userID)(selectionSet(() => SepaMandat, 1)) : of([])),
    [props.userID],
  );

  if (sepaMandatsLoading || sepaMandatTemplatesLoading || faultyMandatsLoading) {
    return html` <div class="flex flex-row flex-wrap gap-4 m-4">
      ${SepaCardSkeleton()} ${SepaCardSkeleton()} ${SepaCardSkeleton()} ${SepaCardSkeleton()}
    </div>`;
  }

  const allMandates = withTemplates(sepaMandats ?? [], sepaMandatTemplates ?? []);

  /**
   * Every mandate belongs into exactly one section, so all three come from the same lifecycle.
   * Sorting a generated template in by its origin instead would list it twice as soon as it is not
   * pending - a funded one for example.
   */
  const inSection = (section: MandateSection) => allMandates.filter(m => getMandateLifecycle(m).section === section);
  const templates = inSection('pending');
  const active = inSection('active');
  const ended = inSection('archive');

  const faulty = faultyMandats ?? [];

  const sectionCount = [templates.length, active.length, ended.length, faulty.length].filter(Boolean).length;
  // open the archive if it is the only section
  const archiveOpenByDefault = sectionCount === 1 && ended.length > 0;

  const mandateGrid = (mandates: SepaMandat[]) => html`
    <div class="flex flex-row flex-wrap gap-4">
      ${repeat(
        mandates,
        m => uniqueID(m),
        m => SepaMandateCard({ mandat: m, userID: props.userID }),
      )}
    </div>
  `;

  const summary = html`
    <div class="flex flex-wrap items-center gap-2">
      ${
        templates.length
          ? html`<div class="badge badge-warning gap-1">
              <span class="icon-[material-symbols--pending-actions]"></span>
              ${templates.length} ausstehend
            </div>`
          : nothing
      }
      ${
        active.length
          ? html`<div class="badge badge-success badge-outline gap-1">
              <span class="icon-[material-symbols--check-circle-outline]"></span>
              ${active.length} aktiv
            </div>`
          : nothing
      }
      ${
        ended.length
          ? html`<div class="badge badge-neutral badge-outline gap-1">
              <span class="icon-[material-symbols--history]"></span>
              ${ended.length} beendet
            </div>`
          : nothing
      }
      ${
        faulty.length
          ? html`<div class="badge badge-error badge-outline gap-1">
              <span class="icon-[material-symbols--error-outline]"></span>
              ${faulty.length} fehlerhaft
            </div>`
          : nothing
      }
    </div>
  `;

  if (!sectionCount) {
    return html`
      <div class="card m-4">
        <div class="card-body items-center text-center gap-2">
          <span class="icon-[material-symbols--account-balance-wallet-outline] h-10 w-10 text-base-content/40"></span>
          <h3 class="font-semibold text-base-content">Keine SEPA-Mandate vorhanden</h3>
          <p class="text-sm text-base-content/70">
            Sobald SEPA-Mandate für diese Person verfügbar sind, erscheinen sie hier.
          </p>
        </div>
      </div>
    `;
  }

  return html`
    <div class="flex flex-col gap-4 m-4">
      ${summary}
      ${
        templates.length
          ? Collapse({
              heading: html`Zustimmung ausstehend<span class="block text-sm font-normal text-base-content/70"
                  >Diesen SEPA-Mandaten können Sie zustimmen. Prüfen Sie die Angaben und bestätigen Sie das
                  Mandat.</span
                >`,
              defaultOpen: true,
              content: mandateGrid(templates),
              className: 'border-warning/50',
            })
          : nothing
      }
      ${
        active.length
          ? Collapse({
              heading: html`Aktive Mandate<span class="block text-sm font-normal text-base-content/70"
                  >Aktuell aktive und geplante SEPA-Mandate. Sie können hier jederzeit Änderungen vornehmen.</span
                >`,
              defaultOpen: true,
              content: mandateGrid(active),
            })
          : nothing
      }
      ${
        ended.length
          ? Collapse({
              heading: html`Beendete Mandate<span class="block text-sm font-normal text-base-content/70"
                  >Abgelaufene SEPA-Mandate, zum Beispiel einmalige Zahlungen in der Vergangenheit oder persönlich
                  beendete Mandate.</span
                >`,
              defaultOpen: archiveOpenByDefault,
              content: mandateGrid(ended),
            })
          : nothing
      }
      ${
        faulty.length
          ? Collapse({
              heading: html`Fehlerhafte Mandate<span class="block text-sm font-normal text-base-content/70"
                  >Von dieser Person akzeptierte SEPA-Mandate auf Buchungen, zu denen sie keine Verbindung mehr hat, zum
                  Beispiel nach dem Entfernen einer Kind-Verknüpfung.</span
                >`,
              content: mandateGrid(faulty),
              className: 'border-error/50',
            })
          : nothing
      }
    </div>
  `;
});
